import { ScreenData } from "../types/ScreenTypes";
import { PrayerTimes } from "../types/PrayerTypes";

// Mock screen data with complete property coverage for testing
export const mockScreenData: ScreenData = {
  // Base properties
  id: 1,
  code: "test-97828556",
  name: "Test Masjid Display",
  masjidId: 3868,
  backgroundTemplate: "template1",
  backgroundType: "template",
  backgroundUrl:
    "https://placehold.co/1920x1080/16213e/ffffff?text=Test+Background",
  isActive: true,
  isResponsive: true,
  orientation: "landscape",
  layoutData: null,
  layoutType: "standard",
  splitTemplate: null,
  width: 1920,
  height: 1080,
  dateCreated: "2023-01-01T00:00:00Z",
  dateModified: "2023-01-01T00:00:00Z",

  // Prayer Length Settings (in seconds)
  fajrLength: 540, // 9 minutes
  dhuhrLength: 360, // 6 minutes
  asarLength: 360, // 6 minutes
  maghribLength: 360, // 6 minutes
  ishaLength: 480, // 8 minutes
  jummahLength: 900, // 15 minutes

  // Reminder Settings
  salahCountdownReminder: 30, // 30 seconds

  // Makrooh Time Settings (in minutes)
  sunriseMakroohPeriod: 10, // 10 minutes after sunrise
  zawwalMakroohPeriod: 5, // 5 minutes before noon
  sunsetMakroohPeriod: 2, // 2 minutes before sunset

  // Screen Blanking Settings - Fajr
  blankScreenDuringFajr: false,
  fajrBlankStartOffset: 1,
  fajrBlankDuration: 8,

  // Screen Blanking Settings - Dhuhr
  blankScreenDuringDhuhr: false,
  dhuhrBlankStartOffset: 1,
  dhuhrBlankDuration: 5,

  // Screen Blanking Settings - Asar
  blankScreenDuringAsar: false,
  asarBlankStartOffset: 1,
  asarBlankDuration: 5,

  // Screen Blanking Settings - Maghrib
  blankScreenDuringMaghrib: false,
  maghribBlankStartOffset: 1,
  maghribBlankDuration: 6,

  // Screen Blanking Settings - Isha
  blankScreenDuringIsha: false,
  ishaBlankStartOffset: 1,
  ishaBlankDuration: 6,

  // Screen Blanking Settings - Jummah
  blankScreenDuringJummah: false,
  jummahBlankStartOffset: 1,
  jummahBlankDuration: 15,

  // Display Settings
  changeIslamicDateAtMaghrib: true,
  showEvents: true,
  eventPosterDuration: 10, // 10 seconds per poster
  showLandscapePosterOnPortrait: true, // Show landscape posters on portrait screens
  showPortraitPosterOnLandscape: true, // Show portrait posters on landscape screens
  showNextSalahChanges: true,
  nextSalahChangesDuration: 10, // 10 seconds
  timesRefreshInterval: 15, // 15 minutes refresh interval

  // Customized Names
  customizedNames: {
    // Prayer time names
    date: "Date",
    day: "Day",
    hijriDate: "Hijri Date",
    sehri: "Sehri",
    fajr: "Fajr",
    sunrise: "Sunrise",
    noon: "Noon",
    dhuhr: "Dhuhr",
    asar: "Asar",
    sunset: "Sunset",
    maghrib: "Maghrib",
    isha: "Isha",
    jummah: "Jummah",

    // Islamic month names
    muharram: "Muharram",
    safar: "Safar",
    rabeeUlAwwal: "Rabee Ul Awwal",
    rabeeUlAkhir: "Rabee Ul Akhir",
    jamadahAlAwwal: "Jamadah Al Awwal",
    jamadahAlAkhir: "Jamadah Al Akhir",
    rajab: "Rajab",
    shaban: "Shaban",
    ramadhan: "Ramadhan",
    shawwal: "Shawwal",
    dhulQadah: "Dhul Qadah",
    dhulHijjah: "Dhul Hijjah",
  },
  prayerPageDuration: 60, // 60 seconds
  salahInProgressMessage: "Salah in Progress",
  fajrAdhanOffset: 20,
  dhuhrAdhanOffset: 15,
  asarAdhanOffset: 15,
  maghribAdhanOffset: 0,
  ishaAdhanOffset: 12,
  jummahAdhanOffset: 5,
  adhanDurationSeconds: 180,
  jummahAdhanDurationSeconds: 300,
};

// Mock prayer times
export const mockPrayerTimes: PrayerTimes = {
  Date: "2023-04-26",
  BegFajr: "04:12",
  JamFajr: "04:45",
  Sunrise: "05:51",
  BegDhuhr: "13:07",
  JamDhuhr: "13:30",
  BegAsar: "17:15",
  BegAsar2: "17:45",
  JamAsar: "17:45",
  Sunset: "20:24",
  BegMaghrib: "20:24",
  JamMaghrib: "20:24",
  BegEsha: "22:04",
  JamEsha: "22:30",
  Jummah1: "13:30",
  HijriDate: "6 Ramadan 1444",
  GregorianDate: "26 April 2023",
  SehriEnd: "04:00",
  Noon: "13:12",
  Month: "8",
  Year: "2025",
};

// Mock layout data
export const mockLayoutData = {
  components: [
    {
      id: "fajrBox",
      type: "text",
      content: "Fajr",
      position: { x: 100, y: 200 },
      size: { width: 150, height: 50 },
      style: { fontSize: 30, fontFamily: "Arial" },
    },
    {
      id: "dhuhrBox",
      type: "text",
      content: "Dhuhr",
      position: { x: 300, y: 200 },
      size: { width: 150, height: 50 },
      style: { fontSize: 30, fontFamily: "Arial" },
    },
    {
      id: "currentTimeBox",
      type: "text",
      content: "Current Time",
      position: { x: 500, y: 100 },
      size: { width: 200, height: 70 },
      style: { fontSize: 40, fontFamily: "monospace" },
    },
    // Add more components here for a complete layout
    {
      id: "asrBox",
      type: "text",
      content: "Asr",
      position: { x: 500, y: 200 },
      size: { width: 150, height: 50 },
      style: { fontSize: 30, fontFamily: "Arial" },
    },
    {
      id: "maghribBox",
      type: "text",
      content: "Maghrib",
      position: { x: 700, y: 200 },
      size: { width: 150, height: 50 },
      style: { fontSize: 30, fontFamily: "Arial" },
    },
    {
      id: "ishaBox",
      type: "text",
      content: "Isha",
      position: { x: 900, y: 200 },
      size: { width: 150, height: 50 },
      style: { fontSize: 30, fontFamily: "Arial" },
    },
    {
      id: "hijriDateBox",
      type: "text",
      content: "Hijri Date",
      position: { x: 700, y: 100 },
      size: { width: 350, height: 50 },
      style: { fontSize: 24, fontFamily: "Arial" },
    },
  ],
  settings: {
    backgroundUrl:
      "https://placehold.co/1920x1080/333/ffffff?text=Custom+Layout",
    isDarkMode: true,
    width: 1920,
    height: 1080,
    theme: "dark",
    fontFamily: "Arial",
  },
};

// Mock API functions for development
export const useMockApi = false; // Set to false to use real API

// Override fetch functions with mock data
export function setupMockApi() {
  if (useMockApi) {
    // Replace the real API functions with mocks
    const originalFetch = window.fetch;
    window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
      const url = input.toString();

      // Mock responses based on URL
      if (url.includes("/api/DigitalSignage/screens/")) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              ok: true,
              json: () => Promise.resolve(mockScreenData),
            } as Response);
          }, 500);
        });
      }

      if (url.includes("/api/DigitalSignage/prayerTimes/")) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              ok: true,
              json: () => Promise.resolve(mockPrayerTimes),
            } as Response);
          }, 500);
        });
      }

      if (url.includes("/api/DigitalSignage/changes/")) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              ok: true,
              json: () => Promise.resolve({ hasChanges: false }),
            } as Response);
          }, 500);
        });
      }

      // Fall back to real fetch for unhandled requests
      return originalFetch(input, init);
    };

    console.log("Mock API is enabled for development");
  }
}
